import React, { useMemo } from "react";

// Renders a job description readably, whatever shape it arrives in.
//
// Descriptions reach us in three different states, measured across the cache: some still carry HTML,
// some are plain text with line breaks, and some are a single flattened run with no structure at all
// (the upstream `_strip_html_text` collapses every whitespace character, so paragraphs, bullets and
// headings are already gone by the time they are stored). Rendered naively in a <div> they become an
// unbroken wall of prose with "Job Family Group: Technology" buried mid-sentence between runs of
// ASCII dashes, which is what ATS exports use as section rules.
//
// So this does not assume structure — it reconstructs what it can:
//   • HTML, when present, is converted with block tags becoming line breaks
//   • runs of dashes/underscores are section separators, not content
//   • "Label: value" is the dominant shape in ATS exports and gets the label bolded
//   • a long line with labels run together is split before each one
//
// Deliberately NOT dangerouslySetInnerHTML: this text comes from third-party job boards and there is
// no sanitiser in the project. Everything below renders as React children, so no markup can execute.

const SEPARATOR = /^[\s\-–—_=*•·]{3,}$/;
const BULLET = /^[•·▪◦*]\s+|^[-–]\s+/;
// "Job Family Group:", "Education:", "Time Type:" — a capitalised label of a few words, then a colon.
const LABEL = /^([A-Z][A-Za-z0-9 /&'’()+.-]{1,44}):[ \t]*(.*)$/;
// Headings that ATS exports write bare, with no colon.
const BARE_HEADING = /^(responsibilities|qualifications|requirements|what you['’]?ll do|what you['’]?ll bring|about (the role|us|the team)|benefits|perks|who you are|the role|skills|experience|education|overview|summary|duties|compensation|why join us)\b[^.]{0,40}$/i;

// Field names ATS exports write inline, in the order Workday and friends emit them. Used ONLY to
// decide where to break a flattened line — see the note at the split site.
const ATS_FIELDS = [
  "Application Deadline", "Address", "Job Family Group", "Job Family", "Job Sub Family",
  "Time Type", "Primary Location", "Locations?", "Full Time Salary Range", "Salary Range",
  "Pay Range", "Base Pay Range", "Compensation", "Most Relevant Skills", "Other Relevant Skills",
  "Education", "Experience", "Job Type", "Employment Type", "Department", "Division",
  "Reports To", "Travel", "Work Arrangement", "Posted Date", "Posted On", "Req(?:uisition)? ID",
  "Shift", "Schedule", "Benefits", "Job Description", "Responsibilities", "Qualifications",
  "Requirements", "Skills", "Job Posting End Date", "Remote Type", "Worker Sub-Type",
];
const ATS_FIELD_SPLIT = new RegExp(`(?=\\b(?:${ATS_FIELDS.join("|")}):[ \\t])`, "g");

const decode = (s) => {
  const ta = document.createElement("textarea");   // decodes entities WITHOUT eating newlines
  ta.innerHTML = s;
  return ta.value;
};

export function jobTextToLines(raw) {
  let t = raw || "";
  if (/<(p|div|ul|ol|li|br|h[1-6]|section|table)\b/i.test(t)) {
    t = t.replace(/<\s*(br|\/p|\/div|\/li|\/ul|\/ol|\/h[1-6]|\/tr|\/section)\b[^>]*>/gi, "\n")
         .replace(/<\s*(p|div|ul|ol|li|h[1-6]|tr|section)\b[^>]*>/gi, "\n")
         .replace(/<[^>]+>/g, " ");
    t = decode(t);
  }
  // ATS section rules: "------------ Job Family Group: Technology ------------"
  t = t.replace(/[ \t]*[-–—_=]{3,}[ \t]*/g, "\n");
  // Bullets written inline (common once HTML is flattened) get their own line.
  t = t.replace(/[ \t]+([•·▪◦])[ \t]+/g, "\n$1 ");

  const out = [];
  for (let line of t.split(/\r?\n/)) {
    line = line.replace(/[ \t ]+/g, " ").trim();
    if (!line || SEPARATOR.test(line)) continue;
    // A flattened line usually has several "Label: value" pairs run together. Split before each one
    // — but ONLY at a known field name. A generic /[A-Z][\w ]+:/ lookahead cannot tell where an
    // address stops and a label starts, so "100 King Street West Job Family Group:" shatters into
    // six fragments at King, Street, West, Job, Family. A false split is worse than no split, so
    // this trades a little coverage for never mangling a line.
    if (line.length > 140) {
      const parts = line.split(ATS_FIELD_SPLIT).map((p) => p.trim()).filter(Boolean);
      if (parts.length > 1) { out.push(...parts); continue; }
    }
    out.push(line);
  }
  return out;
}

function Block({ line }) {
  const m = line.match(LABEL);
  if (m && m[1].split(" ").length <= 6) {
    const [, label, value] = m;
    // A label with nothing after it is a section heading; with a value it is a field.
    return value
      ? <p className="my-1.5"><span className="font-semibold text-[#0F172A]">{label}:</span> {value}</p>
      : <h4 className="font-bold text-[#0F172A] text-[14px] mt-4 mb-1">{label}</h4>;
  }
  if (line.length <= 60 && BARE_HEADING.test(line)) {
    return <h4 className="font-bold text-[#0F172A] text-[14px] mt-4 mb-1">{line}</h4>;
  }
  return <p className="my-1.5">{line}</p>;
}

export default function JobDescription({ text, className = "" }) {
  // Consecutive bullets are gathered into one <ul>. Emitting loose <li> elements straight into a
  // <div> is invalid markup and leaves the list without its own indentation or spacing.
  const groups = useMemo(() => {
    const out = [];
    for (const line of jobTextToLines(text)) {
      if (BULLET.test(line)) {
        const item = line.replace(BULLET, "");
        if (out.length && out[out.length - 1].type === "list") out[out.length - 1].items.push(item);
        else out.push({ type: "list", items: [item] });
      } else {
        out.push({ type: "block", line });
      }
    }
    return out;
  }, [text]);
  if (!groups.length) return null;
  return (
    <div className={`text-[13.5px] text-[#0F172A]/90 leading-relaxed ${className}`}>
      {groups.map((g, i) => g.type === "list"
        ? <ul key={i} className="list-disc pl-5 my-1.5 space-y-1 marker:text-[#0F172A]/30">
            {g.items.map((it, j) => <li key={j}>{it}</li>)}
          </ul>
        : <Block key={i} line={g.line} />)}
    </div>
  );
}
